/**
 * f12.3: the cycle page's snapshot history (Finance and Admin, design D8).
 *
 * One row per freeze, newest first, as the server lists them; `is_current`
 * (the baseline pointer) is the server's. The last reopen reason comes from
 * the cycle body's `last_reopen` (f10.1).
 */

import { History, Lock } from 'lucide-react';

import type { Cycle } from '@/api/client';

import { formatMoney } from '../review/reviewFormat';
import { releaseDate } from '../steps/release/releaseView';
import { useSnapshots } from './useFreeze';

const COUNT = new Intl.NumberFormat('en-US');

function LastReopen({ cycle }: { cycle: Cycle }) {
  const reopen = cycle.last_reopen;
  if (!reopen) return null;
  return (
    <div className="alert alert-neutral snapshot-reopen" role="note">
      <History aria-hidden="true" />
      <div>
        <div className="a-title">
          Last reopened {releaseDate(reopen.reopened_at)} by {reopen.reopened_by.display_name}
        </div>
        <div className="a-body">{reopen.reason}</div>
      </div>
    </div>
  );
}

export function SnapshotHistoryCard({ cycle }: { cycle: Cycle }) {
  const snapshots = useSnapshots(cycle.id);
  // Refused (a role outside Finance/Admin) or not loaded yet: no card.
  if (!snapshots.data) return null;
  const items = snapshots.data.items;

  return (
    <div className="card" data-testid="snapshot-history">
      <div className="card-head">
        <h3>Snapshots</h3>
        <span className="hint">Immutable · newest first</span>
      </div>
      <div className="card-body stack-sm">
        <LastReopen cycle={cycle} />
        {items.length === 0 ? (
          <div className="empty">
            <div className="e-icon">
              <Lock aria-hidden="true" />
            </div>
            <h3>No snapshot yet</h3>
            <p>Locking the cycle takes its first immutable snapshot.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Snapshot</th>
                  <th>Taken</th>
                  <th>By</th>
                  <th className="num">Lines</th>
                  <th className="num">Committed FY (USD)</th>
                  <th className="num">USD rate</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className="mono">#{item.sequence}</span>
                      {item.is_current && <span className="badge badge-primary snapshot-current">Current</span>}
                    </td>
                    <td>{releaseDate(item.taken_at)}</td>
                    <td>{item.taken_by.display_name}</td>
                    <td className="num">{COUNT.format(item.line_count)}</td>
                    <td className="num mono">{formatMoney(item.usd_total)}</td>
                    <td className="num mono">{item.usd_rate_to_eur}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
